'use client'

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAirdrop } from '../hooks/useAirdrop'
import LuckyIcon from '../components/lucky/LuckyIcon'
import CoinGlyph from '../components/lucky/CoinGlyph'
import { TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { COIN_TYPES } from '../types'
import { showToast } from '../components/ui/Toast'
import { coinNameKey, formatNumber, formatUsefulCountdown, getTimeUntilGameEnd } from '../utils/helpers'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'

interface CoinParticle {
  id: number
  x: number
  y: number
}

export default function AirdropPage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale, dir } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const {
    status,
    loading,
    error,
    wallet,
    click,
    watchAd,
    fetchLeaderboard,
    fetchMonthlyLeaderboard,
    leaderboard: liveLeaderboard,
    isConnected,
    refetch,
  } = useAirdrop()
  const [clicking, setClicking] = useState(false)
  const [watchingAd, setWatchingAd] = useState(false)
  const [leaderboard, setLeaderboard] = useState<any[]>(liveLeaderboard)
  const [monthlyLeaders, setMonthlyLeaders] = useState<any[]>([])
  const [loadingLeaders, setLoadingLeaders] = useState(false)
  const [loadingMonthly, setLoadingMonthly] = useState(false)
  const [boardTab, setBoardTab] = useState<'today' | 'monthly'>('today')
  const [berlinClosed, setBerlinClosed] = useState(() => getTimeUntilGameEnd().isClosed)
  const [usefulCountdown, setUsefulCountdown] = useState('05:00')
  const [cooldownCountdown, setCooldownCountdown] = useState('05:00')
  const [coinParticles, setCoinParticles] = useState<CoinParticle[]>([])
  const [tapScale, setTapScale] = useState(1)
  const [scorePop, setScorePop] = useState(false)

  const loadLeaderboard = useCallback(async () => {
    setLoadingLeaders(true)
    try {
      const data = await fetchLeaderboard(10)
      setLeaderboard(data)
    } finally {
      setLoadingLeaders(false)
    }
  }, [fetchLeaderboard])

  const loadMonthlyLeaders = useCallback(async () => {
    setLoadingMonthly(true)
    try {
      const data = await fetchMonthlyLeaderboard(undefined, 10)
      setMonthlyLeaders(data)
    } catch {
      setMonthlyLeaders([])
    } finally {
      setLoadingMonthly(false)
    }
  }, [fetchMonthlyLeaderboard])

  useEffect(() => {
    setLeaderboard(liveLeaderboard)
  }, [liveLeaderboard])

  useEffect(() => {
    loadLeaderboard()
    loadMonthlyLeaders()
  }, [loadLeaderboard, loadMonthlyLeaders])

  useEffect(() => {
    const tick = () => setBerlinClosed(getTimeUntilGameEnd().isClosed)
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let remaining = status?.seconds_remaining ?? 5 * 60
    setUsefulCountdown(formatUsefulCountdown(remaining))

    if (!status?.is_game_active || status.time_exhausted || !status.presence_active) {
      return
    }

    const id = setInterval(() => {
      remaining = Math.max(0, remaining - 1)
      setUsefulCountdown(formatUsefulCountdown(remaining))
    }, 1000)
    return () => clearInterval(id)
  }, [status?.seconds_remaining, status?.presence_active, status?.is_game_active, status?.time_exhausted])

  useEffect(() => {
    let remaining = status?.cooldown_seconds_remaining ?? 0
    setCooldownCountdown(formatUsefulCountdown(remaining))

    if (!status?.is_game_active || !status.time_exhausted || remaining <= 0) {
      return
    }

    const id = setInterval(() => {
      remaining = Math.max(0, remaining - 1)
      setCooldownCountdown(formatUsefulCountdown(remaining))
      if (remaining <= 0) {
        void refetch()
      }
    }, 1000)
    return () => clearInterval(id)
  }, [status?.cooldown_seconds_remaining, status?.time_exhausted, status?.is_game_active, refetch])

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isConnected) return
    if (berlinClosed || !status?.can_click || !status?.is_game_active || status?.time_exhausted) return

    const rect = e.currentTarget.getBoundingClientRect()
    const newParticle: CoinParticle = {
      id: Date.now() + Math.random(),
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    }

    setCoinParticles((prev) => [...prev.slice(-9), newParticle])
    setTapScale(1.08)
    setTimeout(() => setTapScale(1), 150)
    setTimeout(() => {
      setCoinParticles((prev) => prev.filter((p) => p.id !== newParticle.id))
    }, 800)

    setClicking(true)
    try {
      const prevScore = status?.user_score ?? 0
      const result = await click()
      // Only celebrate real accepted clicks (auto-click bursts may be ignored).
      if (result && (result.score_updated || result.score > prevScore)) {
        setScorePop(true)
        setTimeout(() => setScorePop(false), 1000)
      }
      if (result && !result.can_click) {
        void refetch()
      }
    } catch {
      /* ignore */
    } finally {
      setClicking(false)
    }
  }

  const handleWatchAd = async () => {
    if (watchingAd || !status?.can_watch_ad) return
    setWatchingAd(true)
    try {
      const data = await watchAd()
      if (data && data.can_click && !data.time_exhausted) {
        showToast(t('airdrop.toastUsefulCharged'), 'success')
        await refetch()
      }
    } finally {
      setWatchingAd(false)
    }
  }

  if (loading) {
    return (
      <div className="lucky-airdrop mini-app-container pb-20 min-h-screen flex justify-center items-center">
        <div className="lucky-airdrop__empty">{t('common.loading')}</div>
      </div>
    )
  }

  const coins = wallet?.coins || { bronze: 0, silver: 0, gold: 0, diamond: 0 }
  // Client Berlin close gates UI even if the API still thinks UTC is open (missing tzdata).
  const gameOpen = !!status?.is_game_active && !berlinClosed
  const inCooldown = gameOpen && !!status?.time_exhausted
  const canPlay = gameOpen && !status?.time_exhausted && !!status?.can_click

  return (
    <div className="lucky-airdrop mini-app-container pb-20 min-h-screen">
      <div className="lucky-airdrop__inner">
        <header className="lucky-airdrop__header">
          <button type="button" className="lucky-airdrop__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-airdrop__header-icon">
            <LuckyIcon name="gift" size={32} alt="" />
          </div>
          <div>
            <h1>{t('airdrop.title')}</h1>
            <p>{t('airdrop.subtitle')}</p>
          </div>
        </header>

        {error && <div className="lucky-airdrop__alert">{error}</div>}

        {wallet && (
          <section className="lucky-airdrop__card">
            <h2 className="lucky-airdrop__section-title">{t('airdrop.wallet')}</h2>
            <div className="lucky-airdrop__coins">
              {COIN_TYPES.map((type) => (
                <div key={type} className="lucky-airdrop__coin">
                  <CoinGlyph type={type} size={22} />
                  <strong>{formatNumber(coins[type] ?? 0, numberLocale)}</strong>
                  <span>{t(coinNameKey(type))}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="lucky-airdrop__card lucky-airdrop__play">
          <div className="lucky-airdrop__score-block">
            <span>{t('airdrop.scoreToday')}</span>
            <strong>{formatNumber(status?.user_score || 0, numberLocale)}</strong>
          </div>

          {gameOpen ? (
            <div className="lucky-airdrop__timer">
              <span>
                {inCooldown
                  ? t('airdrop.waitNextTurn')
                  : t('airdrop.usefulTimerActive')}
              </span>
              <strong>{inCooldown ? cooldownCountdown : usefulCountdown}</strong>
            </div>
          ) : (
            <div className="lucky-airdrop__timer">
              <span>{t('airdrop.closedHours')}</span>
              <strong>{t('airdrop.berlinTime')}</strong>
            </div>
          )}

          <div className={`lucky-airdrop__tap-wrap ${canPlay ? '' : 'is-locked'}`}>
            {canPlay &&
              coinParticles.map((p) => (
                <div
                  key={p.id}
                  className="lucky-airdrop__particle"
                  style={{ left: p.x, top: p.y }}
                >
                  <CoinGlyph type="gold" size={22} />
                </div>
              ))}

            {canPlay && scorePop && <div className="lucky-airdrop__pop">+1</div>}

            <div className="lucky-airdrop__tap-stage">
              <button
                type="button"
                className="lucky-airdrop__tap"
                onClick={handleClick}
                disabled={!canPlay || clicking || !isConnected || !status?.can_click}
                style={{ transform: canPlay ? `scale(${tapScale})` : undefined }}
                aria-label={canPlay ? t('airdrop.tapAriaActive') : t('airdrop.tapAriaLocked')}
              >
                <CoinGlyph type="gold" size={72} />
                <em>{formatNumber(status?.user_score || 0, numberLocale)}</em>
              </button>

              {!canPlay && (
                <div className="lucky-airdrop__glass" role="status">
                  <strong>{!gameOpen ? t('airdrop.glassClosed') : t('airdrop.glassCooldown')}</strong>
                  <span>
                    {!gameOpen
                      ? t('airdrop.glassClosedDetail')
                      : t('airdrop.glassCooldownDetail', { time: cooldownCountdown })}
                  </span>
                </div>
              )}
            </div>

            {inCooldown && status?.can_watch_ad && (
              <button
                type="button"
                className="lucky-game__ad"
                style={{ marginTop: 12, width: '100%' }}
                disabled={watchingAd}
                onClick={handleWatchAd}
              >
                {watchingAd ? t('common.watchingAd') : t('airdrop.watchAdInstant')}
              </button>
            )}

            <p className="lucky-airdrop__tap-hint">
              {canPlay
                ? clicking
                  ? t('airdrop.hintRegistering')
                  : !isConnected
                    ? t('airdrop.hintConnecting')
                    : status?.presence_active
                      ? t('airdrop.hintTimerRunning')
                      : t('airdrop.hintTimerPaused')
                : !gameOpen
                  ? t('airdrop.hintNightlyClosed')
                  : t('airdrop.hintCooldownOrAd')}
            </p>
          </div>
        </section>

        <section className="lucky-airdrop__card">
          <div className="lucky-airdrop__tabs" role="tablist" aria-label={t('airdrop.leadersTablistAria')}>
            <button
              type="button"
              role="tab"
              aria-selected={boardTab === 'today'}
              className={`lucky-airdrop__tab ${boardTab === 'today' ? 'is-on' : ''}`}
              onClick={() => setBoardTab('today')}
            >
              <TrophyGlyph size={16} />
              {t('airdrop.leadersToday')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={boardTab === 'monthly'}
              className={`lucky-airdrop__tab ${boardTab === 'monthly' ? 'is-on' : ''}`}
              onClick={() => setBoardTab('monthly')}
            >
              <LuckyIcon name="calendar" size={16} alt="" />
              {t('airdrop.leadersMonth')}
            </button>
          </div>

          {boardTab === 'today' ? (
            loadingLeaders ? (
              <div className="lucky-airdrop__empty">{t('common.loading')}</div>
            ) : leaderboard.length === 0 ? (
              <div className="lucky-airdrop__empty">{t('airdrop.emptyNoScores')}</div>
            ) : (
              <div className="lucky-airdrop__board">
                {dedupeLeaders(leaderboard).map((entry) => (
                  <LeaderRow key={entry.user_id} entry={entry} />
                ))}
              </div>
            )
          ) : loadingMonthly ? (
            <div className="lucky-airdrop__empty">{t('common.loading')}</div>
          ) : monthlyLeaders.length === 0 ? (
            <div className="lucky-airdrop__empty">{t('airdrop.emptyNoData')}</div>
          ) : (
            <div className="lucky-airdrop__board">
              {dedupeLeaders(monthlyLeaders).map((entry) => (
                <LeaderRow key={entry.user_id} entry={entry} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

/** Keep first row per user so duplicate ranks never render. */
function dedupeLeaders(entries: any[]) {
  const seen = new Set<number>()
  const out: any[] = []
  for (const entry of entries || []) {
    const id = Number(entry?.user_id)
    if (!Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)
    out.push({ ...entry, rank: out.length + 1 })
  }
  return out
}

function LeaderRow({ entry }: { entry: any }) {
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  return (
    <div className="lucky-airdrop__row">
      <span className="lucky-airdrop__rank">{formatNumber(entry.rank, numberLocale)}</span>
      {entry.photo_url ? (
        <img src={entry.photo_url} alt="" className="lucky-airdrop__avatar" />
      ) : (
        <div className="lucky-airdrop__avatar lucky-airdrop__avatar--fallback">
          {entry.first_name?.[0] || '?'}
        </div>
      )}
      <strong className="lucky-airdrop__name">{entry.first_name || entry.username || t('common.user')}</strong>
      <em>{formatNumber(entry.score, numberLocale)}</em>
    </div>
  )
}
